import * as XLSX from 'xlsx';

// =============================================================================
// customerZonesExcel — Export / import for the Customer → Zone mapping.
//
// Modelled on utils/volumesExcel.js: an export that is also the import
// template, a reference sheet so nobody has to guess a zone code, and parsing
// that reports EVERY bad row at once rather than dying on the first.
//
// The export carries the full result of a re-query, not what is on screen. The
// grid holds at most a page, and handing someone a 100-line file that looks
// complete is how a re-import quietly unmaps everyone else.
// =============================================================================

const SHEET = 'Customer Zones';
const REF_SHEET = 'Zones';

const HEADERS = [
  'Customer Code',
  'Customer Name',
  'Channel',
  'Zone Code',
  'Zone Name',
  'Avg Drop Size',
  'Active',
];

// Column widths chosen so the sheet opens readable — a template people have to
// resize before they can read it is a template they edit in the wrong cell.
const COL_WIDTHS = [{ wch: 18 }, { wch: 34 }, { wch: 20 }, { wch: 14 }, { wch: 26 }, { wch: 14 }, { wch: 9 }];

export function exportCustomerZones({ rows = [], zones = [], versionLabel = '' }) {
  const data = rows.map((r) => ({
    'Customer Code': r.customer_code,
    'Customer Name': r.customer_name,
    Channel: r.channel_name || '',
    'Zone Code': r.zone_code || '',
    'Zone Name': r.zone_name || '',
    // Blank rather than 0 for an unmapped customer: 0 is a value somebody could
    // believe, and the importer rejects it anyway.
    'Avg Drop Size': r.zone_code ? Number(r.avg_drop_size) || '' : '',
    Active: r.zone_code ? (r.is_active === false ? 'N' : 'Y') : '',
  }));

  const ws = XLSX.utils.json_to_sheet(data, { header: HEADERS });
  ws['!cols'] = COL_WIDTHS;

  const refRows = zones.map((z) => ({
    'Zone Code': z.zone_code,
    'Zone Name': z.zone_name,
    Type: z.zone_kind === 'STORE' ? 'Store cluster' : 'Customer',
    'Served From': z.origin_site_code || '',
    'Load Unit': z.load_uom,
    'Round-trip km': Number(z.round_trip_km) || 0,
  }));
  const refWs = XLSX.utils.json_to_sheet(refRows);
  refWs['!cols'] = [{ wch: 14 }, { wch: 30 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 14 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, SHEET);
  XLSX.utils.book_append_sheet(wb, refWs, REF_SHEET);

  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `Customer_Zones_${versionLabel || 'budget'}_${stamp}.xlsx`);
}

// Returns { rows, errors }. Rows are ready for secondaryService.saveAssignments.
//
// An empty Zone Code is NOT an error — it is how the sheet says "this customer
// is not delivered", and the save endpoint unmaps it. That is deliberate: the
// alternative is deleting rows out of a spreadsheet, which nobody does
// reliably and which no one can review afterwards.
export async function parseCustomerZones(file, { zones = [], customers = [] } = {}) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[SHEET] || wb.Sheets[wb.SheetNames[0]];
  if (!ws) return { rows: [], errors: ['No readable sheet in that file.'] };

  const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
  const zoneCodes = new Set(zones.map((z) => String(z.zone_code)));
  const customerCodes = new Set(customers.map((c) => String(c.customer_code)));

  const rows = [];
  const errors = [];
  const seen = new Set();

  json.forEach((r, i) => {
    const line = i + 2;   // +1 for the header, +1 because spreadsheets are 1-based
    const customer = String(r['Customer Code'] ?? '').trim();
    if (!customer) return;   // a genuinely blank row is padding, not an error

    if (customerCodes.size && !customerCodes.has(customer)) {
      errors.push(`Row ${line}: customer "${customer}" is not in the customer master.`);
      return;
    }
    // The 1:1 rule has to hold in the FILE too, or the last row silently wins
    // and the user never learns which one was applied.
    if (seen.has(customer)) {
      errors.push(`Row ${line}: customer "${customer}" appears more than once — a customer belongs to exactly one zone.`);
      return;
    }
    seen.add(customer);

    const zone = String(r['Zone Code'] ?? '').trim();
    if (!zone) { rows.push({ customer_code: customer, zone_code: null }); return; }

    if (zoneCodes.size && !zoneCodes.has(zone)) {
      errors.push(`Row ${line}: zone "${zone}" does not exist. Check the ${REF_SHEET} sheet.`);
      return;
    }

    const raw = r['Avg Drop Size'];
    const size = Number(raw);
    if (raw === '' || raw === null || raw === undefined) {
      errors.push(`Row ${line}: "${customer}" has a zone but no average drop size — drops are volume ÷ drop size.`);
      return;
    }
    if (!Number.isFinite(size) || size <= 0) {
      errors.push(`Row ${line}: "${customer}" has an average drop size of "${raw}" — it must be a number greater than zero.`);
      return;
    }

    const active = String(r.Active ?? 'Y').trim().toUpperCase();
    rows.push({
      customer_code: customer,
      zone_code: zone,
      avg_drop_size: size,
      is_active: !(active === 'N' || active === 'NO' || active === 'FALSE'),
    });
  });

  return { rows, errors };
}
